import { z } from 'zod'
import { fetchWithNetworkRetry } from '@/lib/client-fetch'
import type { MarketMoneyRange } from '@/lib/market-money'
import { productTypeLabel } from '@/lib/warehouse/labels'

export const UNIT_ECONOMICS_ENDPOINT = '/api/analytics/unit-economics'
export const UNIT_ECONOMICS_PIECE_ENDPOINT = '/api/analytics/unit-economics/pieces'
// Longer ranges are charted by week, then by month, so the points stay readable.
export const DAILY_CHART_MAX_DAYS = 45
export const WEEKLY_CHART_MAX_DAYS = 190

const money = z.number()
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const isoMonth = z.string().regex(/^\d{4}-\d{2}$/)
const chargeSchema = z.object({
  kind: z.string(),
  label: z.string(),
  cash: money,
  rate: z.number().nullable(),
})
const withholdingSchema = z.object({
  kind: z.string(),
  label: z.string(),
  amount: money,
})
const feeColumnSchema = z.object({
  key: z.enum(['services', 'boost', 'penalties', 'withholdings']),
  label: z.string(),
  chargeKinds: z.array(z.string()),
})
const metalAveragesSchema = z.object({ gold: money.nullable(), silver: money.nullable() })

const rowSchema = z.object({
  orderId: z.string(),
  shopSku: z.string(),
  productId: z.string().nullable(),
  photoUrl: z.string().nullable(),
  photoReason: z.string().nullable(),
  article: z.string().nullable(),
  uin: z.string().nullable(),
  weightGrams: z.number().nullable(),
  metal: z.string().nullable(),
  fineness: z.string().nullable(),
  category: z.string().nullable(),
  program: z.string().nullable(),
  orderCreatedAt: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  tariff: z.enum(['old', 'new']),
  boosted: z.boolean(),
  boostRate: z.number().nullable(),
  placementRate: z.number().nullable(),
  tariffPlacementRate: z.number().nullable(),
  listPrice: money.nullable(),
  crossedPrice: money.nullable(),
  promoPrice: money.nullable(),
  orderPrice: money,
  buyerPaid: money,
  deliveryPaid: money,
  refunded: money,
  charges: z.array(chargeSchema),
  withholdings: z.array(withholdingSchema),
  marketGross: money,
  marketMoney: money,
  marketPoints: money,
  marketDiscountCredit: money,
  marketWithheld: money,
  received: money,
  soldFor: money,
  cash: money,
  pending: z.boolean(),
  bankOrders: z.array(z.object({ id: z.number().int(), date: isoDate.nullable(), matches: z.boolean().nullable() })),
  cost: money.nullable(),
  costReason: z.string().nullable(),
  costDocument: z.string().nullable(),
  costReceiptDate: isoDate.nullable(),
  costSource: z.enum(['onec', 'owner_default_rate']).nullable(),
  costEstimateRate: money.nullable(),
  costEstimateBasis: z.enum(['transfer_retail', 'rate']).nullable(),
  costOnecReason: z.string().nullable(),
  cdekCost: money.nullable(),
  cdekReason: z.string().nullable(),
  cdekWaybills: z.array(z.object({ number: z.string(), direction: z.string(), total: money.nullable() })),
  adSpend: money,
  adReason: z.string(),
  revenue: money,
  tax: money.nullable(),
  taxReason: z.string().nullable(),
  profit: money.nullable(),
  profitPerGram: money.nullable(),
  profitReason: z.string().nullable(),
  margin: money.nullable(),
  marginOfCost: z.number().nullable(),
  marginOfSale: z.number().nullable(),
  profitability: z.number().nullable(),
  taxLabel: z.string(),
})

const totalsSchema = z.object({
  sold: z.number().int(),
  turnover: money,
  buyerPaid: money,
  withheld: money,
  boost: money,
  received: money,
  cash: money,
  cost: money,
  cdek: money,
  adSpend: money,
  tax: money,
  profit: money,
  bought: money,
  soldFor: money,
  margin: money,
  marginOfCost: z.number().nullable(),
  marginOfSale: z.number().nullable(),
  receivedWithoutCost: money,
  incomplete: z.number().int(),
  profitability: z.number().nullable(),
  costMissing: z.number().int(),
  costEstimated: z.number().int(),
  uncounted: z.number().int(),
  negativeReceived: z.number().int(),
  costReasons: z.array(z.object({ reason: z.string(), count: z.number().int() })),
  medianProfitPerGram: money.nullable(),
  soldForPerGram: metalAveragesSchema,
  costPerGram: metalAveragesSchema,
  averageProfit: money.nullable(),
  profitRate: z.number().nullable(),
  marketFeeRate: z.number().nullable(),
  averageCheck: money.nullable(),
})

const reportSchema = z.object({
  periodFrom: isoDate,
  periodTo: isoDate,
  taxLabel: z.string(),
  costEstimateRate: money.nullable(),
  collection: z.object({
    collectedAt: z.string().nullable(),
    rangeFrom: isoDate.nullable(),
    rangeTo: isoDate.nullable(),
    bankOrders: z.number().int(),
    bankOrdersMatched: z.number().int(),
  }),
  categories: z.array(z.string()),
  totals: totalsSchema,
  months: z.array(z.object({ month: isoMonth, category: z.string().nullable(), totals: totalsSchema })),
  monthChecks: z.array(z.object({
    month: isoMonth,
    received: money,
    linesTotal: money,
    cash: money,
    settledCash: money,
    compensation: money,
    withheldFromCompensation: money,
    matches: z.boolean(),
  })),
  feeColumns: z.array(feeColumnSchema),
  rows: z.array(rowSchema),
})

export type UnitEconomicsReport = z.infer<typeof reportSchema>
export type UnitEconomicsRow = z.infer<typeof rowSchema>
export type UnitTotals = z.infer<typeof totalsSchema>
export type UnitMonth = UnitEconomicsReport['months'][number]
export type UnitMonthCheck = UnitEconomicsReport['monthChecks'][number]
export type UnitBoostFilter = 'boosted' | 'not_boosted'
export type UnitTariffFilter = 'old' | 'new'

export const UNIT_SERVICE_CHARGE_KINDS: ReadonlySet<string> = new Set(['placement', 'transfer', 'acceptance', 'logistics'])
export type UnitChargePart = 'cash'

function chargeValue(charge: UnitEconomicsRow['charges'][number], _part: UnitChargePart): number {
  return charge.cash
}

function sumCharges(
  row: Pick<UnitEconomicsRow, 'charges'>,
  include: (kind: string) => boolean,
  part: UnitChargePart,
): number {
  return row.charges.reduce((total, charge) => total + (include(charge.kind) ? chargeValue(charge, part) : 0), 0)
}

export function unitChargeAmount(row: Pick<UnitEconomicsRow, 'charges'>, kind: string, part: UnitChargePart = 'cash'): number | null {
  let found = false
  let total = 0
  for (const charge of row.charges) {
    if (charge.kind !== kind) continue
    found = true
    total += chargeValue(charge, part)
  }
  return found ? total : null
}

export function unitServiceAmount(row: Pick<UnitEconomicsRow, 'charges'>, part: UnitChargePart = 'cash'): number {
  return sumCharges(row, (kind) => UNIT_SERVICE_CHARGE_KINDS.has(kind), part)
}

export function unitPenaltyAmount(row: Pick<UnitEconomicsRow, 'charges'>, part: UnitChargePart = 'cash'): number {
  return sumCharges(row, (kind) => kind === 'penalty', part)
}

export function unitOtherAmount(row: Pick<UnitEconomicsRow, 'charges'>, part: UnitChargePart = 'cash'): number {
  return sumCharges(row, (kind) => !UNIT_SERVICE_CHARGE_KINDS.has(kind) && kind !== 'boost' && kind !== 'penalty', part)
}

export interface UnitEconomicsFilters {
  range: MarketMoneyRange
  category: string | null
  boost: UnitBoostFilter | null
  tariff: UnitTariffFilter | null
  costMissing: boolean
  negative: boolean
}

export type UnitServerFilters = Omit<UnitEconomicsFilters, 'range'>

export const DEFAULT_UNIT_FILTERS: UnitServerFilters = {
  category: null,
  boost: null,
  tariff: null,
  costMissing: false,
  negative: false,
}

export function unitEconomicsUrl(filters: UnitEconomicsFilters): string {
  const query = new URLSearchParams({ from: filters.range.from, to: filters.range.to })
  if (filters.category) query.set('category', filters.category)
  if (filters.boost) query.set('boost', filters.boost)
  if (filters.tariff) query.set('tariff', filters.tariff)
  if (filters.costMissing) query.set('cost', 'missing')
  if (filters.negative) query.set('received', 'negative')
  return `${UNIT_ECONOMICS_ENDPOINT}?${query.toString()}`
}

export async function fetchUnitEconomics(filters: UnitEconomicsFilters, signal?: AbortSignal): Promise<UnitEconomicsReport> {
  const response = await fetchWithNetworkRetry(unitEconomicsUrl(filters), { signal })
  if (!response.ok) throw new Error('Не удалось загрузить юнит-экономику.')
  const parsed = reportSchema.safeParse(await response.json())
  if (!parsed.success) throw new Error('Сервер вернул юнит-экономику в неожиданном формате.')
  return parsed.data
}

export async function fetchUnitEconomicsPiece(productId: string, signal?: AbortSignal): Promise<UnitEconomicsRow | null> {
  const response = await fetchWithNetworkRetry(`${UNIT_ECONOMICS_PIECE_ENDPOINT}/${encodeURIComponent(productId)}`, { signal })
  if (response.status === 404) return null
  if (!response.ok) throw new Error('Не удалось загрузить экономику изделия.')
  const parsed = rowSchema.safeParse(await response.json())
  if (!parsed.success) throw new Error('Сервер вернул экономику изделия в неожиданном формате.')
  return parsed.data
}

export const UNCATEGORIZED_CATEGORY = 'Без категории'

/** The warehouse's Russian name for a product-type code. */
export function categoryLabel(category: string): string {
  return category === UNCATEGORIZED_CATEGORY ? category : productTypeLabel(category)
}

export function formatRub(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return `${Math.round(value).toLocaleString('ru-RU')} ₽`
}

export function formatKopecks(value: number): string {
  return `${value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`
}

export function formatPercent(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return '—'
  return `${(rate * 100).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%`
}

/** «92,4%»: a month's money share with one decimal. */
/** The buyer's money for the piece: payment and delivery less refunds. */
export function buyerMoney(row: Pick<UnitEconomicsRow, 'buyerPaid' | 'deliveryPaid' | 'refunded'>): number {
  return row.buyerPaid + row.deliveryPaid + row.refunded
}

/** Rubles per gram of the piece's 1C weight, or null when either is unknown. */
export function perGram(value: number | null, weightGrams: number | null): number | null {
  if (value === null || weightGrams === null || weightGrams <= 0) return null
  return value / weightGrams
}

export function formatPerGram(value: number | null): string {
  return value === null ? '—' : `${Math.round(value).toLocaleString('ru-RU')} ₽/г`
}

/** Formats the already-computed owner estimate rate without losing its precision. */
export function formatEstimateRate(value: number): string {
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽/г`
}

/** Green above zero, red below, neutral at zero or when unknown. */
export function signClass(value: number | null): string {
  if (value === null || value === 0) return 'text-ink-muted'
  return value > 0 ? 'text-[#1f7a3d]' : 'text-[#b3261e]'
}

export interface ProfitabilityStatus {
  label: string
  className: string
}

/** The Market unit-economics workbook's bands for profit over the price for calculations. */
export function profitabilityStatus(rate: number | null): ProfitabilityStatus | null {
  if (rate === null || !Number.isFinite(rate)) return null
  if (rate < 0) return { label: 'Убыточно', className: 'bg-[#b3261e]/10 text-[#b3261e]' }
  if (rate < 0.05) return { label: 'Низко/на грани', className: 'bg-[#b86e00]/10 text-[#9a5c00]' }
  if (rate < 0.12) return { label: 'Умеренно', className: 'bg-gold/15 text-gold-deep' }
  if (rate < 0.2) return { label: 'Хорошо', className: 'bg-[#1f7a3d]/10 text-[#1f7a3d]' }
  return { label: 'Отлично', className: 'bg-[#1f7a3d]/20 text-[#1f7a3d]' }
}

export type UnitSortKey = 'date' | 'cost' | 'soldFor' | 'marketWithheld' | 'boost' | 'penalty' | 'otherCharges' | 'received' | 'margin' | 'marginOfCost' | 'cdekCost' | 'tax' | 'profit' | 'profitability'

export function sortUnitRows(rows: readonly UnitEconomicsRow[], key: UnitSortKey, ascending: boolean): UnitEconomicsRow[] {
  const value = (row: UnitEconomicsRow): number | null => {
    if (key === 'date') return row.orderCreatedAt ? Date.parse(row.orderCreatedAt) : null
    if (key === 'marketWithheld') return unitServiceAmount(row)
    if (key === 'penalty') return unitChargeAmount(row, 'penalty')
    if (key === 'otherCharges') return unitOtherAmount(row)
    if (key === 'boost') return unitChargeAmount(row, 'boost')
    if (key === 'cdekCost') return row.cdekCost
    if (key === 'tax') return row.tax
    return row[key]
  }
  return [...rows].sort((left, right) => {
    const a = value(left)
    const b = value(right)
    if (a === null && b === null) return left.orderId.localeCompare(right.orderId)
    if (a === null) return 1
    if (b === null) return -1
    return ascending ? a - b : b - a
  })
}

export function moscowDate(instant: string): string {
  return new Date(instant).toLocaleDateString('en-CA', { timeZone: 'Europe/Moscow' })
}

function addDays(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

function mondayOf(isoDate: string): string {
  const weekday = (new Date(`${isoDate}T00:00:00Z`).getUTCDay() + 6) % 7
  return addDays(isoDate, -weekday)
}

export type UnitChartStep = 'day' | 'week' | 'month'

export interface UnitChartPoint {
  from: string
  to: string
  sold: number
  bought: number
  soldFor: number
  margin: number
  cumulativeMargin: number
  withoutCost: number
}

function monthEnd(isoDate: string): string {
  const date = new Date(Date.UTC(Number(isoDate.slice(0, 4)), Number(isoDate.slice(5, 7)), 0))
  return date.toISOString().slice(0, 10)
}

function bucketStart(isoDate: string, step: UnitChartStep): string {
  if (step === 'week') return mondayOf(isoDate)
  if (step === 'month') return `${isoDate.slice(0, 7)}-01`
  return isoDate
}

/** Bought for, sold for and net margin per day, week or month of order creation; empty buckets stay as zero points. */
export function chartPoints(rows: readonly UnitEconomicsRow[], range: MarketMoneyRange): { step: UnitChartStep; points: UnitChartPoint[] } {
  const days = Math.round((Date.parse(`${range.to}T00:00:00Z`) - Date.parse(`${range.from}T00:00:00Z`)) / 86_400_000) + 1
  const step: UnitChartStep = days <= DAILY_CHART_MAX_DAYS ? 'day' : days <= WEEKLY_CHART_MAX_DAYS ? 'week' : 'month'
  const points = new Map<string, UnitChartPoint>()
  for (let start = bucketStart(range.from, step); start <= range.to;) {
    const end = step === 'day' ? start : step === 'week' ? addDays(start, 6) : monthEnd(start)
    points.set(start, {
      from: start < range.from ? range.from : start,
      to: end > range.to ? range.to : end,
      sold: 0, bought: 0, soldFor: 0, margin: 0, cumulativeMargin: 0, withoutCost: 0,
    })
    start = addDays(end, 1)
  }
  for (const row of rows) {
    if (!row.orderCreatedAt) continue
    const point = points.get(bucketStart(moscowDate(row.orderCreatedAt), step))
    if (!point) continue
    point.sold += 1
    if (row.cost === null) {
      point.withoutCost += 1
    } else {
      point.bought += row.cost
      point.soldFor += row.soldFor
      point.margin += row.margin ?? (row.received - row.cost)
    }
  }
  let running = 0
  const ordered = [...points.values()]
  for (const point of ordered) {
    running += point.margin
    point.cumulativeMargin = running
  }
  return { step, points: ordered }
}

export function rowsInSpan(rows: readonly UnitEconomicsRow[], span: { from: string; to: string } | null): UnitEconomicsRow[] {
  if (!span) return [...rows]
  return rows.filter((row) => {
    if (!row.orderCreatedAt) return false
    const day = moscowDate(row.orderCreatedAt)
    return day >= span.from && day <= span.to
  })
}

/** «оценка 6 000 ₽/г»: the owner's per-gram rate that prices a piece 1C gives no cost. */
export function costEstimateLabel(rate: number | null): string {
  return rate === null ? 'оценка' : `оценка ${rate.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽/г`
}

/** A 2020 opening-balance piece is estimated from its transfer retail, any other from its ₽/г. */
export function costEstimateNote(row: { costEstimateBasis: 'transfer_retail' | 'rate' | null; costEstimateRate: number | null }): string {
  return row.costEstimateBasis === 'transfer_retail' ? 'оценка: розница 2020 ÷ 2' : costEstimateLabel(row.costEstimateRate)
}
